import type { TableContainerProps } from "@chakra-ui/react"
import {
  Button,
  Center,
  Flex,
  Link,
  Skeleton,
  Stack,
  Table,
  TableContainer,
  Tag,
  TagLabel,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react"
import { useEffect, useState } from "react"
import { useDebounce } from "use-debounce"
import flatten from "lodash.flatten"

import { ROUTES } from "common/constants/routes"
import { labelNumber } from "common/helpers/number"
import { useDevice } from "common/hooks/useDevice"
import GovernanceAvatar from "governance/components/GovernanceAvatar"
import type {
  ChainId,
  ExploreOrgsQuery,
  ExploreOrgsQueryVariables,
  Organization,
} from "query/graphql"
import {
  ExploreOrgsDocument,
  OrganizationsSortBy,
  useExploreSearchOrgsQuery,
} from "query/graphql"
import { getWhiteLabelRoute } from "whitelabel/utils/breadcrumb"
import EmptyEntity from "common/components/EmptyEntity"
import LoadMoreButton from "common/components/LoadMoreButton"
import { useInfiniteCursorQuery } from "query/hooks/useInfiniteQuery"
import { subString } from "common/helpers/string"
import { isMultiChainTokenIds } from "organization/helpers/organization"
import NetworkTag from "common/components/NetworkTag"
import { getAssetIdParams } from "web3/helpers/assetId"
import { ORGANIZATION_PRIVATE_ACCESS_SLUGS } from "organization/constants/private-orgs"

type TableOrg = Pick<
  Organization,
  | "hasActiveProposals"
  | "chainIds"
  | "governorIds"
  | "metadata"
  | "name"
  | "proposalsCount"
  | "slug"
  | "tokenOwnersCount"
  | "delegatesCount"
  | "tokenIds"
>

const OrganizationTable = ({
  searchString,
  chainId,
  isWhitelabel = false,
  ...tableContainerProps
}: {
  searchString?: string
  chainId?: ChainId
  isWhitelabel?: boolean
} & TableContainerProps) => {
  const { onLargeDevice, onLittleDevice } = useDevice()
  const [debouncedSearchString] = useDebounce(searchString, 1000)

  const shouldSearch = !!(
    debouncedSearchString && debouncedSearchString.length > 2
  )

  const PAGE_SIZE = 20

  const {
    data: exploreData,
    fetchNextPage,
    hasNextPage,
    isLoading: isExploreLoading,
    isFetching: isExploreFetching,
  } = useInfiniteCursorQuery<ExploreOrgsQuery, ExploreOrgsQueryVariables>({
    document: ExploreOrgsDocument,
    sectionName: "ExploreOrgsDocument",
    nextPagePath: "organizations",
    variables: {
      input: {
        sort: {
          isDescending: true,
          sortBy: OrganizationsSortBy.Explore,
        },
        filters: {
          chainId: chainId,
          hasLogo: true,
        },
        page: { limit: PAGE_SIZE },
      },
    },
  })

  const {
    data: searchData,
    isLoading: isExploreSearchLoading,
    isFetching: isExploreSearchIsFetching,
  } = useExploreSearchOrgsQuery(
    {
      input: {
        name: debouncedSearchString || "",
        filters: {
          chainId: chainId,
        },
      },
    },
    {
      enabled: shouldSearch,
    },
  )

  const [orgs, setOrgs] = useState<TableOrg[]>([])

  useEffect(() => {
    if (shouldSearch && searchData) {
      setOrgs(
        searchData.searchOrganization
          ?.filter((organization) => organization.tokenIds?.length > 0)
          // Hide private DAOs from search results
          .filter(
            (organization) =>
              !ORGANIZATION_PRIVATE_ACCESS_SLUGS.includes(organization.slug),
          )
          .sort((a, b) =>
            a.tokenOwnersCount &&
            b.tokenOwnersCount &&
            a.tokenOwnersCount > b.tokenOwnersCount
              ? -1
              : 1,
          ),
      )

      return
    }

    if (exploreData) {
      const organizations = flatten(
        exploreData?.pages.map(
          ({ organizations }) => organizations.nodes ?? [],
        ),
      )
      setOrgs(organizations as TableOrg[])
    }
  }, [shouldSearch, searchData, exploreData])

  const isLoading =
    (isExploreLoading && isExploreFetching) ||
    (isExploreSearchLoading && isExploreSearchIsFetching)

  const isLoadMoreEnabled = !(shouldSearch && searchData)

  if (isLoading) {
    return (
      <Stack mt={6}>
        <Skeleton h="40px" w="full" />
        <Skeleton h="60px" w="full" />
        <Skeleton h="60px" w="full" />
        <Skeleton h="60px" w="full" />
        <Skeleton h="60px" w="full" />
        <Skeleton h="60px" w="full" />
      </Stack>
    )
  }

  if (orgs.length === 0) {
    return <EmptyEntity mt={0} title="No DAOs found" />
  }

  return (
    <Stack>
      <TableContainer
        bg="white"
        borderBottom="1px solid hsla(220, 17%, 93%, 1)"
        borderX="1px solid hsla(220, 17%, 93%, 1)"
        display={onLargeDevice} // large device
        roundedBottom="10px"
        roundedTop="10px"
        {...tableContainerProps}
      >
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th color="neutral.600" fontSize="14px" lineHeight="20px">
                Name
              </Th>
              <Th
                isNumeric
                color="neutral.600"
                fontSize="14px"
                lineHeight="20px"
              >
                Proposals
              </Th>
              <Th
                isNumeric
                color="neutral.600"
                fontSize="14px"
                lineHeight="20px"
              >
                Holders
              </Th>
              <Th
                isNumeric
                color="neutral.600"
                fontSize="14px"
                lineHeight="20px"
              >
                Voters
              </Th>
              <Th />
            </Tr>
          </Thead>
          <Tbody>
            {isLoading ? (
              <>
                <Tr>
                  <Td>
                    <Skeleton h="20px" w="full" />
                  </Td>
                  <Td>
                    <Skeleton h="20px" w="full" />
                  </Td>
                  <Td>
                    <Skeleton h="20px" w="full" />
                  </Td>
                  <Td>
                    <Skeleton h="20px" w="full" />
                  </Td>
                  <Td>
                    <Skeleton h="20px" w="full" />
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    <Skeleton h="20px" w="full" />
                  </Td>
                  <Td>
                    <Skeleton h="20px" w="full" />
                  </Td>
                  <Td>
                    <Skeleton h="20px" w="full" />
                  </Td>
                  <Td>
                    <Skeleton h="20px" w="full" />
                  </Td>
                  <Td>
                    <Skeleton h="20px" w="full" />
                  </Td>
                </Tr>
              </>
            ) : (
              <>
                {orgs.map((org) => {
                  if (!org) return null

                  const {
                    name,
                    slug,
                    metadata,
                    hasActiveProposals,
                    governorIds,
                    proposalsCount,
                    tokenOwnersCount,
                    delegatesCount,
                  } = org

                  const getChainData = () => {
                    // Org has no governors, but it has tokens
                    if (org?.tokenIds && org.tokenIds.length > 0) {
                      const isMultiChain = isMultiChainTokenIds(org.tokenIds)
                      const tokenId = org.tokenIds[0]
                      const { chainId } = getAssetIdParams(tokenId)

                      return { chainId, isMultiChain }
                    }

                    return { chainId: undefined, isMultiChain: false }
                  }

                  const { chainId, isMultiChain } = getChainData()

                  const isRari = governorIds.includes(
                    "eip155:1:0x6552C8fb228f7776Fc0e4056AA217c139D4baDa1",
                  )

                  return (
                    <Tr key={`org_row_item_${org.slug}`}>
                      <Td>
                        <Stack
                          align="center"
                          direction="row"
                          flex={{ base: 1, lg: 3 }}
                          spacing={4}
                        >
                          <GovernanceAvatar size={10} src={metadata?.icon} />
                          <Link href={ROUTES.governance.id(slug)}>
                            <Text
                              color="gray.900"
                              fontSize="16px"
                              fontWeight="medium"
                              lineHeight="24px"
                            >
                              {subString(name, 25)}
                            </Text>
                          </Link>
                          {hasActiveProposals ? <ActiveProposalTag /> : null}
                        </Stack>
                      </Td>
                      <Td isNumeric>
                        <Text
                          color="gray.900"
                          fontSize="16px"
                          fontWeight="medium"
                          lineHeight="24px"
                        >
                          {labelNumber(proposalsCount)}
                        </Text>
                      </Td>
                      <Td isNumeric>
                        <Text
                          color="gray.900"
                          fontSize="16px"
                          fontWeight="medium"
                          lineHeight="24px"
                        >
                          {isRari ? "N/A" : labelNumber(tokenOwnersCount)}
                        </Text>
                      </Td>
                      <Td isNumeric>
                        <Text
                          color="gray.900"
                          fontSize="16px"
                          fontWeight="medium"
                          lineHeight="24px"
                        >
                          {labelNumber(delegatesCount)}
                        </Text>
                      </Td>
                      <Td>
                        <Flex align="center" justify="flex-end" w="full">
                          {chainId ? (
                            <NetworkTag
                              chainId={chainId}
                              isMultiChain={isMultiChain}
                            />
                          ) : null}
                        </Flex>
                      </Td>
                    </Tr>
                  )
                })}
              </>
            )}
            {isLoadMoreEnabled && hasNextPage ? (
              <Tr>
                <Td colSpan={5}>
                  <LoadMoreButton
                    isDisabled={!hasNextPage}
                    width="100%"
                    onClick={() => fetchNextPage()}
                  />
                </Td>
              </Tr>
            ) : null}
          </Tbody>
        </Table>
      </TableContainer>

      <TableContainer
        bg="white"
        borderBottom="1px solid hsla(220, 17%, 93%, 1)"
        borderX="1px solid hsla(220, 17%, 93%, 1)"
        display={onLittleDevice} // little device
        roundedBottom="10px"
        roundedTop="10px"
        {...tableContainerProps}
      >
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th color="neutral.600" fontSize="14px" lineHeight="20px">
                Name
              </Th>
              <Th
                isNumeric
                color="neutral.600"
                fontSize="14px"
                lineHeight="20px"
              >
                Voters
              </Th>
              <Th
                isNumeric
                color="neutral.600"
                fontSize="14px"
                lineHeight="20px"
              >
                Voting Power
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {orgs.map((org) => {
              if (!org) return null

              const {
                name,
                slug,
                metadata,
                hasActiveProposals,
                delegatesCount,
              } = org

              return (
                <Tr key={`governancne_row_item_${slug}`}>
                  <Td>
                    <Stack
                      align="center"
                      direction="row"
                      flex={{ base: 1, lg: 3 }}
                      spacing={4}
                    >
                      <GovernanceAvatar size={10} src={metadata?.icon} />
                      <Link href={ROUTES.governance.id(slug)}>
                        <Text
                          color="gray.900"
                          fontSize="16px"
                          fontWeight="medium"
                          lineHeight="24px"
                        >
                          {subString(name, 20)}
                        </Text>
                      </Link>
                      {hasActiveProposals ? <ActiveProposalTag /> : null}
                    </Stack>
                  </Td>
                  <Td isNumeric>
                    <Text
                      color="gray.900"
                      fontSize="16px"
                      fontWeight="medium"
                      lineHeight="24px"
                    >
                      {labelNumber(delegatesCount)}
                    </Text>
                  </Td>
                  <Td isNumeric>
                    {/* This will need to support multi governors */}

                    <Button
                      as={Link}
                      bgColor="white"
                      borderColor="gray.300"
                      borderWidth="1px"
                      className="no-underline"
                      href={
                        isWhitelabel
                          ? getWhiteLabelRoute(
                              ROUTES.governance.myVotingPower.index(slug),
                            )
                          : ROUTES.governance.myVotingPower.index(slug)
                      }
                    >
                      Review
                    </Button>
                  </Td>
                </Tr>
              )
            })}
            {isLoadMoreEnabled && hasNextPage ? (
              <Tr>
                <Td>
                  <Center>
                    <LoadMoreButton
                      isDisabled={!hasNextPage}
                      onClick={() => fetchNextPage()}
                    />
                  </Center>
                </Td>
              </Tr>
            ) : null}
          </Tbody>
        </Table>
      </TableContainer>
    </Stack>
  )
}

export default OrganizationTable

const ActiveProposalTag = () => {
  return (
    <Tag bg="primary.50" size="sm" variant="subtle">
      <TagLabel color="primary.600" lineHeight="12px">
        Active proposal
      </TagLabel>
    </Tag>
  )
}
